import React, { useState, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { TarotCard } from './TarotCard';
import axios from 'axios';
import './App.css';
import { ProgressChart } from './ProgressChart';
import { useRef } from 'react';
import { toPng } from 'html-to-image';
// import { ShareableSummary } from './ShareableSummary';

const API_URL = 'https://ec2-3-87-125-214.compute-1.amazonaws.com';

function App() {
  const [puuid, setPuuid] = useState('');
  const [loading, setLoading] = useState(false);
  const [reading, setReading] = useState(null);
  const [error, setError] = useState('');
  const [flippedCards, setFlippedCards] = useState([false, false, false]); // Track which cards are flipped
  const [progressData, setProgressData] = useState(null);
  // const [showShareModal, setShowShareModal] = useState(false);
  // const shareableRef = useRef();

  const handleDivine = async () => {
    if (!puuid) {
      setError('A PUUID is required to see your fate.');
      return;
    }
    setLoading(true);
    setReading(null);
    setError('');
    setFlippedCards([false, false, false]); // Reset all cards to face-down
    setProgressData(null);

    try {
      // 1. Get the narrative and player summary
      const readingResponse = await axios.get(`${API_URL}/oracle/reading/${puuid}`);
      const { oracle_reading, player_summary } = readingResponse.data;

      // 2. Prepare the requests to generate images
      const imagePromises = player_summary.champions.slice(0, 3).map((champ, index) => {
          let cardTitle = "The Unknown";
          if (index === 0) cardTitle = oracle_reading.past_card.title;
          if (index === 1) cardTitle = oracle_reading.present_card.title;
          if (index === 2) cardTitle = oracle_reading.future_card.title;

          return axios.post(`${API_URL}/oracle/generate-image`, { 
            champion_name: champ.championName, 
            card_title: cardTitle 
          });
      });

      const imageResults = await Promise.all(imagePromises);
      const images = imageResults.map(res => `data:image/png;base64,${res.data.image_base64}`);

      setReading([
        { 
          ...oracle_reading.past_card, 
          image: images[0] || '/card-back.png',
          position: 'Past',
          isFlipped: false
        },
        { 
          ...oracle_reading.present_card, 
          image: images[1] || '/card-back.png',
          position: 'Present',
          isFlipped: false
        },
        { 
          ...oracle_reading.future_card, 
          image: images[2] || '/card-back.png',
          position: 'Future',
          isFlipped: false
        },
      ]);

      const progressResponse = await axios.get(`${API_URL}/oracle/progress/${puuid}`);
      if (progressResponse.data.progress && progressResponse.data.progress.length > 0) {
        setProgressData(progressResponse.data.progress);
      }

    } catch (err) {
      setError(err.response?.data?.detail || 'The Oracle is silent. An unknown error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (index) => {
    if (!reading) return;
    
    const newFlippedCards = [...flippedCards];
    newFlippedCards[index] = !newFlippedCards[index];
    setFlippedCards(newFlippedCards);
  };

  return (
    <div className="App">
      {/* Mystical Background */}
      <div className="mystical-background">
        <div className="stars"></div>
        <div className="constellation"></div>
      </div>

      <header className="App-header">
        <div className="oracle-title">
          <h1>🔮 The Rift Oracle 🔮</h1>
          <div className="mystical-subtitle">
            <p>Enter your Player PUUID to have your season's journey told by the ancient spirits of the Rift.</p>
          </div>
        </div>
        
        <div className="input-container">
          <div className="mystical-input-wrapper">
            <input 
              type="text" 
              value={puuid} 
              onChange={(e) => setPuuid(e.target.value)} 
              placeholder="Enter a PUUID from the database" 
              disabled={loading}
              className="mystical-input"
            />
            <div className="input-glow"></div>
          </div>
          <button 
            onClick={handleDivine} 
            disabled={loading}
            className="mystical-button"
          >
            {loading ? (
              <>
                <span className="loading-text">Consulting the Cosmos...</span>
                <div className="mystical-spinner"></div>
              </>
            ) : (
              'Divine Your Fate'
            )}
          </button>
        </div>
        {error && <p className="error-message">{error}</p>}
      </header>

      <div className="canvas-container">
        <Canvas camera={{ position: [0, 2, 12], fov: 60 }}>
          <ambientLight intensity={0.8} />
          <pointLight position={[10, 10, 10]} intensity={1.2} color="#8B5CF6" />
          <pointLight position={[-10, -10, -10]} intensity={0.8} color="#06B6D4" />
          <Suspense fallback={null}>
            {reading && (
              <>
                <TarotCard 
                  position={[-4.5, 0, 0]} 
                  imageUrl={reading[0].image} 
                  cardTitle={reading[0].position}
                  isFlipped={flippedCards[0]}
                  onClick={() => handleCardClick(0)}
                  narrative={reading[0].narrative}
                />
                <TarotCard 
                  position={[0, 0, 0]} 
                  imageUrl={reading[1].image} 
                  cardTitle={reading[1].position}
                  isFlipped={flippedCards[1]}
                  onClick={() => handleCardClick(1)}
                  narrative={reading[1].narrative}
                />
                <TarotCard 
                  position={[4.5, 0, 0]} 
                  imageUrl={reading[2].image} 
                  cardTitle={reading[2].position}
                  isFlipped={flippedCards[2]}
                  onClick={() => handleCardClick(2)}
                  narrative={reading[2].narrative}
                />
              </>
            )}
          </Suspense>
          <OrbitControls enableZoom={false} />
        </Canvas>
      </div>

      {/* Mystical Instructions */}
      {reading && !loading && (
        <div className="mystical-instructions">
          <p>✨ Click on the cards to reveal your destiny ✨</p>
        </div>
      )}

      {/* Display flipped card narratives */}
      {reading && !loading && (
        <div className="narrative-display">
          {reading.map((card, index) => (
            flippedCards[index] && (
              <div key={index} className="card-narrative-mystical">
                <h3 className="card-title-mystical">{card.position}</h3>
                <h4 className="card-subtitle-mystical">{card.title}</h4>
                <p className="card-text-mystical">{card.narrative}</p>
              </div>
            )
          ))}
        </div>
      )}

      {progressData && !loading && (
        <div className="narrative-display">
          <div className="card-narrative-mystical" style={{maxWidth: '900px', width: '100%'}}>
            <ProgressChart progressData={progressData} />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
